/**
 * Product compare dock — floating tray (product-compare-dock).
 */
(function () {
	'use strict';

	const productCompare = window.ProductCompare;

	if (!productCompare?.store || customElements.get('product-compare-dock')) {
		return;
	}

	const {
		CHANGE_EVENT: compareChangeEvent,
		store: productCompareStore,
		compareToast,
		syncCompareToggleButtons,
		getComparePageBaseUrl,
		isSamePath,
		buildComparePageUrl,
		escapeHtmlAttr,
		escapeCompareUrl,
	} = productCompare;

	const COMPARE_ICON_SVG =
		'<svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true"><path d="M4 6h5v10H4zM11 4h5v12h-5z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/></svg>';
	const CTA_ARROW_SVG =
		'<svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true"><path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

	/**
	 * @param {CompareEntry} entry
	 * @param {string} removeLabel
	 * @param {{ showName?: boolean, viewLabelTemplate?: string }} [options]
	 */
	function renderDockThumb(entry, removeLabel, options = {}) {
		const showName = options.showName === true;
		const viewLabelTemplate = options.viewLabelTemplate || 'View {{ title }}';
		const productUrl = escapeCompareUrl(entry.url, entry.handle);
		const safeTitle = escapeHtmlAttr(entry.title);
		const viewLabel = viewLabelTemplate.replace(/\{\{\s*title\s*\}\}/g, entry.title || entry.handle);
		const safeViewLabel = escapeHtmlAttr(viewLabel);

		const image = entry.image
			? `<img src="${escapeHtmlAttr(entry.image)}" alt="" width="46" height="46" loading="lazy" class="product-compare-dock__thumb-img">`
			: '<span class="product-compare-dock__thumb-img product-compare-dock__thumb-img--placeholder" aria-hidden="true"></span>';
		const nameMarkup = showName ? `<span class="product-compare-dock__thumb-name">${escapeHtmlAttr(entry.title)}</span>` : '';

		return `
      <div class="product-compare-dock__thumb">
        <a
          href="${productUrl}"
          class="product-compare-dock__thumb-link"
          title="${safeTitle}"
          aria-label="${safeViewLabel}"
        >
          ${image}
          ${nameMarkup}
        </a>
        <button type="button" class="product-compare-dock__thumb-remove" data-compare-remove="${escapeHtmlAttr(entry.handle)}" aria-label="${escapeHtmlAttr(removeLabel)}">
          <svg viewBox="0 0 14 14" width="11" height="11" aria-hidden="true"><path d="M3.5 3.5l7 7M10.5 3.5l-7 7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
        </button>
      </div>
    `;
	}

	const TAB_POPOVER_DIALOG_ID = 'product-compare-dock-tab-dialog';

	class ProductCompareDock extends HTMLElement {
		initialized = false;
		tabOpen = false;
		tabPopoverListenersBound = false;
		/** @type {HTMLElement | null} */
		tabTriggerElement = null;

		constructor() {
			super();

			this.boundOnChange = () => {
				if (!this.isConnected) {
					return;
				}

				this.#renderDock();
				syncCompareToggleButtons();
			};

			this.boundOnKeydown = this.#onKeydown.bind(this);
			this.boundOnRootClick = this.#onRootClick.bind(this);
			this.boundOnDocumentPointerDown = this.#onDocumentPointerDown.bind(this);
			this.boundOnTabPopoverKeydown = this.#onTabPopoverKeydown.bind(this);
		}

		connectedCallback() {
			if (this.initialized) {
				return;
			}

			this.initialized = true;

			this.readConfig();
			this.cacheElements();
			this.bindEvents();
			this.init();
		}

		disconnectedCallback() {
			this.destroy();
			this.initialized = false;
		}

		readConfig() {
			this.helperSectionId = this.dataset.helperSection || 'helper-product-compare-data';
			this.maxProducts = parseInt(this.dataset.maxProducts || '3', 10);
			this.dockStyle = this.dataset.dockStyle || 'side';
			this.sidePosition = this.dataset.sidePosition || 'right';
			productCompareStore.maxProducts = this.maxProducts;
		}

		cacheElements() {
			this.rootEl = this.querySelector('[data-compare-dock-root]');
		}

		bindEvents() {
			this.rootEl?.addEventListener('click', this.boundOnRootClick);
			document.addEventListener(compareChangeEvent, this.boundOnChange);
			document.addEventListener('keydown', this.boundOnKeydown);
		}

		init() {
			this.classList.add(`product-compare-dock--${this.dockStyle}`);
			this.classList.add(`product-compare-dock--${this.sidePosition}`);
			this.#renderDock();
		}

		destroy() {
			this.#setTabOpen(false);
			this.#unbindTabPopoverListeners();
			document.removeEventListener(compareChangeEvent, this.boundOnChange);
			document.removeEventListener('keydown', this.boundOnKeydown);
			this.rootEl?.removeEventListener('click', this.boundOnRootClick);
		}

		/** @returns {HTMLElement[]} */
		#getTabPopoverFocusables() {
			const tabPop = this.querySelector('.product-compare-dock__tab-pop');

			if (!tabPop) {
				return [];
			}

			return [
				...tabPop.querySelectorAll(
					'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
				),
			];
		}

		#focusTabPopoverFirst() {
			const focusables = this.#getTabPopoverFocusables();

			focusables[0]?.focus();
		}

		#bindTabPopoverListeners() {
			if (this.tabPopoverListenersBound) {
				return;
			}

			this.tabPopoverListenersBound = true;
			document.addEventListener('pointerdown', this.boundOnDocumentPointerDown, true);
			document.addEventListener('keydown', this.boundOnTabPopoverKeydown);
			queueMicrotask(() => this.#focusTabPopoverFirst());
		}

		#unbindTabPopoverListeners() {
			if (!this.tabPopoverListenersBound) {
				return;
			}

			this.tabPopoverListenersBound = false;
			document.removeEventListener('pointerdown', this.boundOnDocumentPointerDown, true);
			document.removeEventListener('keydown', this.boundOnTabPopoverKeydown);
		}

		/** @param {PointerEvent} event */
		#onDocumentPointerDown(event) {
			if (!this.tabOpen || this.dockStyle !== 'tab') {
				return;
			}

			const target = event.target;

			if (target instanceof Node && this.contains(target)) {
				return;
			}

			this.#setTabOpen(false);
		}

		/** @param {KeyboardEvent} event */
		#onTabPopoverKeydown(event) {
			if (!this.tabOpen || this.dockStyle !== 'tab' || event.key !== 'Tab') {
				return;
			}

			const focusables = this.#getTabPopoverFocusables();

			if (focusables.length === 0) {
				return;
			}

			const first = focusables[0];
			const last = focusables[focusables.length - 1];

			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();

				return;
			}

			if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		}

		/** @param {KeyboardEvent} event */
		#onKeydown(event) {
			if (event.key !== 'Escape' || !this.tabOpen) {
				return;
			}

			this.#setTabOpen(false);
		}

		/**
		 * @param {string} template
		 * @param {Record<string, string | number>} vars
		 */
		#formatLabel(template, vars) {
			let text = template || '';

			Object.entries(vars).forEach(([key, value]) => {
				text = text.replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'), String(value));
			});

			return text;
		}

		#setTabOpen(shouldOpen) {
			const wasOpen = this.tabOpen;

			this.tabOpen = shouldOpen;
			this.classList.toggle('product-compare-dock--tab-open', shouldOpen);

			const tabBtn = this.querySelector('[data-compare-tab-toggle]');
			const tabPop = this.querySelector('.product-compare-dock__tab-pop');

			tabBtn?.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');

			if (tabPop) {
				if (shouldOpen) {
					tabPop.removeAttribute('hidden');
				} else {
					tabPop.setAttribute('hidden', '');
				}
			}

			if (this.dockStyle !== 'tab') {
				return;
			}

			if (shouldOpen && !wasOpen) {
				this.tabTriggerElement =
					document.activeElement instanceof HTMLElement ? document.activeElement : tabBtn;
				this.#bindTabPopoverListeners();

				return;
			}

			if (!shouldOpen && wasOpen) {
				this.#unbindTabPopoverListeners();

				const restoreFocus = this.tabTriggerElement;

				this.tabTriggerElement = null;

				if (restoreFocus?.isConnected) {
					restoreFocus.focus();
				}
			}
		}

		#onRootClick(event) {
			const removeBtn = event.target.closest('[data-compare-remove]');

			if (removeBtn) {
				event.preventDefault();
				const handle = removeBtn.dataset.compareRemove;

				if (handle) {
					productCompareStore.remove(handle);
					compareToast('success', 'compare_toast_removed', 'Product removed from comparison list.');
				}

				return;
			}

			if (event.target.closest('[data-compare-dock-clear]')) {
				event.preventDefault();
				productCompareStore.clear();
				this.#setTabOpen(false);

				return;
			}

			if (event.target.closest('[data-compare-dock-compare]')) {
				event.preventDefault();
				this.#goToCompare();

				return;
			}

			if (event.target.closest('[data-compare-tab-toggle]')) {
				event.preventDefault();
				this.#setTabOpen(!this.tabOpen);
			}
		}

		#goToCompare() {
			const handles = productCompareStore.getHandles();

			if (handles.length < 2) {
				compareToast('error', 'compare_need_two', 'Add at least 2 products to compare.');

				return;
			}

			const comparePageUrl = this.dataset.comparePageUrl || getComparePageBaseUrl();
			const compareTable = document.querySelector('product-compare-table');
			const compareSection = document.getElementById('product-compare');

			if (compareTable?.dataset.mode === 'page' && isSamePath(window.location.pathname, comparePageUrl)) {
				compareTable.dispatchEvent(new CustomEvent('compare:run', { bubbles: true }));
				this.#setTabOpen(false);

				return;
			}

			if (compareSection && compareTable && compareTable.dataset.mode === 'product') {
				compareSection.scrollIntoView({ behavior: 'smooth' });
				compareTable.dispatchEvent(new CustomEvent('compare:run', { bubbles: true }));
				this.#setTabOpen(false);

				return;
			}

			window.location.href = buildComparePageUrl(comparePageUrl, handles);
		}

		#renderDockCta(canCompare, entryCount, extraClass = '') {
			const needOneMore = this.dataset.needOneMoreLabel || 'Choose one more';
			const compareCount = this.#formatLabel(this.dataset.compareCountLabel || 'Compare {{ count }}', {
				count: entryCount,
			});
			const label = canCompare ? compareCount : needOneMore;
			const arrow = canCompare ? CTA_ARROW_SVG : '';

			return `<button type="button" class="product-compare-dock__cta ${extraClass}" data-compare-dock-compare ${canCompare ? '' : 'disabled'}>${label}${arrow}</button>`;
		}

		#renderDock() {
			if (!this.rootEl) {
				return;
			}

			const entries = productCompareStore.getEntries();
			const max = this.maxProducts;
			const style = this.dockStyle;
			const removeLabel = this.dataset.removeLabel || 'Remove';
			const clearLabel = this.dataset.clearLabel || 'Clear';
			const clearAllLabel = this.dataset.clearAllLabel || 'Clear all';
			const entryCount = entries.length;

			this.classList.remove(
				'product-compare-dock--side',
				'product-compare-dock--rail',
				'product-compare-dock--bar',
				'product-compare-dock--tab',
			);
			this.classList.add(`product-compare-dock--${style}`);

			if (entryCount === 0) {
				this.setAttribute('hidden', '');
				this.rootEl.innerHTML = '';
				this.#setTabOpen(false);

				return;
			}

			this.removeAttribute('hidden');

			const canCompare = entryCount >= 2;
			const viewLabelTemplate = this.dataset.viewProductLabel || 'View {{ title }}';
			const thumbOptions = { viewLabelTemplate };
			const thumbs = entries.map((entry) => renderDockThumb(entry, removeLabel, thumbOptions)).join('');
			const thumbsNamed = entries.map((entry) => renderDockThumb(entry, removeLabel, { ...thumbOptions, showName: true })).join('');
			const slotLabel = entryCount < 2 ? `+ ${2 - entryCount}` : '+';
			const emptySlot = entryCount < max ? `<div class="product-compare-dock__slot">${slotLabel}</div>` : '';
			const toCompareLabel = this.#formatLabel(this.dataset.toCompareLabel || '{{ count }} to compare', {
				count: entryCount,
			});

			if (style === 'rail') {
				this.rootEl.innerHTML = `
          <div class="product-compare-dock__rail-card">
            <div class="product-compare-dock__rail-thumbs">${thumbs}${emptySlot}</div>
            <div class="product-compare-dock__rail-meta">
              <span class="product-compare-dock__rail-count">${toCompareLabel}</span>
              <button type="button" class="product-compare-dock__clear-text" data-compare-dock-clear>${clearLabel}</button>
            </div>
            ${this.#renderDockCta(canCompare, entryCount)}
          </div>
        `;

				return;
			}

			if (style === 'bar') {
				this.rootEl.innerHTML = `
          <div class="product-compare-dock__bar-inner">
            <div class="product-compare-dock__bar-title">
              ${this.dataset.tabLabel || 'Compare'}<span>${entryCount}/${max}</span>
            </div>
            <div class="product-compare-dock__bar-chips">${thumbsNamed}${entryCount < max ? `<div class="product-compare-dock__slot product-compare-dock__slot--bar">${slotLabel}</div>` : ''}</div>
            <div class="product-compare-dock__bar-actions">
              <button type="button" class="product-compare-dock__clear-link" data-compare-dock-clear>${clearAllLabel}</button>
              ${this.#renderDockCta(canCompare, entryCount)}
            </div>
          </div>
        `;

				return;
			}

			if (style === 'tab') {
				const tabSlotHint = entryCount < 2 ? this.dataset.needOneMoreLabel || '' : `+ ${max - entryCount}`;
				const tabDialogLabel = escapeHtmlAttr(this.dataset.tabLabel || 'Compare');

				this.rootEl.innerHTML = `
          <div
            class="product-compare-dock__tab-pop"
            id="${TAB_POPOVER_DIALOG_ID}"
            role="dialog"
            aria-modal="true"
            aria-label="${tabDialogLabel}"
            ${this.tabOpen ? '' : 'hidden'}
          >
            <div class="product-compare-dock__tab-pop-head">
              <span>${toCompareLabel}</span>
              <button type="button" class="product-compare-dock__clear-text" data-compare-dock-clear>${clearLabel}</button>
            </div>
            <div class="product-compare-dock__tab-pop-list">${thumbsNamed}${entryCount < max ? `<div class="product-compare-dock__tab-slot">${tabSlotHint}</div>` : ''}</div>
            ${this.#renderDockCta(canCompare, entryCount, 'product-compare-dock__cta--full')}
          </div>
          <button type="button" class="product-compare-dock__tab-handle" data-compare-tab-toggle aria-expanded="${this.tabOpen ? 'true' : 'false'}" aria-controls="${TAB_POPOVER_DIALOG_ID}">
            <span class="product-compare-dock__tab-handle-icon">
              ${COMPARE_ICON_SVG}
              <span class="product-compare-dock__tab-badge">${entryCount}</span>
            </span>
            ${this.dataset.tabLabel || 'Compare'}
            <svg class="product-compare-dock__tab-caret" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M4 10l4-4 4 4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        `;

				if (this.tabOpen) {
					queueMicrotask(() => this.#focusTabPopoverFirst());
				}

				return;
			}

			this.rootEl.innerHTML = `
        <div class="product-compare-dock__side-card">
          <div class="product-compare-dock__side-head"><span>${entryCount}</span></div>
          <div class="product-compare-dock__side-thumbs">${thumbs}${emptySlot}</div>
          <button type="button" class="product-compare-dock__side-cta" data-compare-dock-compare ${canCompare ? '' : 'disabled'} title="${canCompare ? this.dataset.compareNowLabel || 'Compare' : this.dataset.needOneMoreLabel || ''}">
            ${COMPARE_ICON_SVG}
          </button>
          <button type="button" class="product-compare-dock__side-clear" data-compare-dock-clear aria-label="${clearAllLabel}">
            <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
          </button>
        </div>
      `;
		}
	}

	customElements.define('product-compare-dock', ProductCompareDock);
})();
